import { useCallback, useEffect, useRef, useState } from "react";

/** Drives the UTSAVA Genesis showreel: loads the static scene manifest and
 * advances scene-by-scene. When sound is on, each scene lasts exactly as long
 * as its narration track; when muted (browsers block audio autoplay) it falls
 * back to the pre-computed `duration_hint`. */
export function useShowreel({ active, muted }) {
    const [scenes, setScenes] = useState([]);
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(true);
    const audioRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        fetch("/showreel/manifest.json")
            .then((r) => r.json())
            .then((d) => { if (!cancelled) setScenes(d.scenes || []); })
            .catch(() => { if (!cancelled) setScenes([]); });
        return () => { cancelled = true; };
    }, []);

    const advance = useCallback(() => {
        setIndex((i) => (scenes.length ? (i + 1) % scenes.length : 0));
    }, [scenes.length]);

    useEffect(() => {
        if (!active || !playing || !scenes.length) return;
        const scene = scenes[index];
        let timer;
        if (!muted) {
            const audio = new Audio(scene.audio);
            audioRef.current = audio;
            audio.onended = advance;
            audio.play().catch(() => { timer = setTimeout(advance, scene.duration_hint * 1000); });
        } else {
            timer = setTimeout(advance, scene.duration_hint * 1000);
        }
        return () => {
            clearTimeout(timer);
            if (audioRef.current) { audioRef.current.onended = null; audioRef.current.pause(); audioRef.current = null; }
        };
    }, [active, playing, muted, scenes, index, advance]);

    const goTo = useCallback((i) => setIndex(i), []);
    return { scenes, scene: scenes[index], index, playing, setPlaying, advance, goTo };
}
